import {
  useAttendMutation,
  useCallNextMutation,
  useCallMutation,
  useFinalizeWithIssuesMutation,
  useFinalizeWithSuccessMutation,
  useMarkAsWaitingMutation,
} from '../../services/totem-sector.service';
import { NotificationSeverity } from '@/components/notifications/Notification';
import { useNotification } from '@/hooks';
import { CallNextAPIResponse, TotemSectorMutationArgs } from '../../models';

const useTotemSectorActions = () => {
  const { showNotification } = useNotification();

  const [callNext, { isLoading: isLoadingCallNext }] = useCallNextMutation();
  const [call, { isLoading: isLoadingCall }] = useCallMutation();

  const saveCallNextResponse = (response: CallNextAPIResponse) => {
    localStorage.setItem('TOTEM_SECTOR_ID', response.trackId);
  };

  const onCallNext = async (sectorId: string) => {
    try {
      const data = await callNext(sectorId).unwrap();
      saveCallNextResponse(data);
      return data;
    } catch (error: any) {
      showNotification(
        error.data.error || 'Error al intentar llamar al siguiente',
        NotificationSeverity.error,
        2000
      );
    }
  };

  const onCall = async (totemSectorBody: TotemSectorMutationArgs) => {
    try {
      const data = await call(totemSectorBody).unwrap();
      saveCallNextResponse(data);
      return data;
    } catch (error: any) {
      showNotification(
        error.data?.error || 'Error al intentar llamar',
        NotificationSeverity.error,
        2000
      );
    }
  };

  const [attend, { isLoading: isLoadingAttend }] = useAttendMutation();

  const onAttendCustomer = async (totemSectorBody: TotemSectorMutationArgs) => {
    try {
      await attend(totemSectorBody).unwrap();
    } catch (error: any) {
      showNotification(
        error.message || 'Error al intentar atender al siguiente',
        NotificationSeverity.error,
        2000
      );
    }
  };

  const [finalizeWithSuccess, { isLoading: isFinalizeWithSuccessLoading }] =
    useFinalizeWithSuccessMutation();
  const [finalizeWithIssues, { isLoading: isFinalizeWithIssuesLoading }] =
    useFinalizeWithIssuesMutation();

  const onFinalizeWithSuccess = async (
    totemSectorBody: TotemSectorMutationArgs
  ) => {
    try {
      await finalizeWithSuccess(totemSectorBody).unwrap();
      showNotification(
        'Finalizado con éxito',
        NotificationSeverity.success,
        2000
      );
    } catch (error: any) {
      console.error('Error al intentar finalizar con éxito: ', error);
      showNotification(
        error.message || 'Error al intentar finalizar con éxito',
        NotificationSeverity.error,
        2000
      );
    }
  };

  const onFinalizeWithIssues = async (
    totemSectorBody: TotemSectorMutationArgs
  ) => {
    try {
      await finalizeWithIssues(totemSectorBody).unwrap();
      showNotification(
        'Ticket finalizado con problemas correctamente',
        NotificationSeverity.success,
        1000
      );
    } catch (error: any) {
      showNotification(
        error.message ?? 'Error al finalizar con problemas',
        NotificationSeverity.error,
        2000
      );
    }
  };

  const [markAsWaiting, { isLoading: isMarkAsWaitingLoading }] =
    useMarkAsWaitingMutation();

  const onMarkAsWaiting = async (totemSectorBody: TotemSectorMutationArgs) => {
    try {
      await markAsWaiting(totemSectorBody).unwrap();
      showNotification(
        'Ticket marcado como ESPERANDO',
        NotificationSeverity.success,
        1000
      );
      localStorage.removeItem('TOTEM_SECTOR_ID');
    } catch (error: any) {
      showNotification(
        error.message ?? 'Error al marcar como WAITING',
        NotificationSeverity.error,
        2000
      );
    }
  };

  return {
    onCallNext,
    isLoadingCallNext,
    onCall,
    isLoadingCall,
    onAttendCustomer,
    isLoadingAttend,
    onFinalizeWithSuccess,
    onFinalizeWithIssues,
    isFinalizing: isFinalizeWithIssuesLoading || isFinalizeWithSuccessLoading,
    onMarkAsWaiting,
    isMarkAsWaitingLoading,
  };
};

export default useTotemSectorActions;
export { useTotemSectorActions };
